/**
 * EM-GIFT-GUARD
 * =============
 * Schuetzt die Gratisgeschenk-Mechanik (Schwellenwert-Geschenke aus dem Cart
 * Drawer) gegen alle Wege, die an der Drawer-Logik vorbeifuehren:
 *
 *   - Swym-Wishlist bzw. "fuer spaeter speichern": Das Geschenk wird von dort
 *     ohne die Marker-Property `_free_gift` erneut in den Warenkorb gelegt und
 *     gilt fuer die bisherige Logik damit als ganz normales 0-EUR-Produkt.
 *   - Produktseite, Suche, Quick-Add, Kollektionen.
 *   - Mengenaenderung auf der Warenkorb-Seite.
 *
 * Die Konfiguration liefert `sections/mr-vue-cart.liquid` als
 * `window.EM_FREE_GIFT_CONFIG`. Den eigentlichen Warenkorb-Abgleich uebernimmt
 * die Vue-Komponente des Drawers (die ueber die overlay-group auf jeder Seite
 * gerendert wird). Dieses Skript ergaenzt:
 *
 *   1. Add-to-Cart-Sperre fuer Geschenkprodukte im gesamten Storefront
 *   2. Checkout-Sperre, solange ungueltige Geschenkzeilen im Warenkorb liegen
 *   3. Reconcile-Trigger nach Warenkorb-Aenderungen durch fremde Apps
 *   4. Fallback-Bereinigung, falls der Drawer nicht vorhanden ist
 *
 * WICHTIG: Das ist ausschliesslich clientseitiger Schutz. Gegen manipulierte
 * Requests (direkter POST auf /cart/add.js aus der Browser-Konsole) hilft nur
 * eine serverseitige Absicherung - Shopify Function "Cart and Checkout
 * Validation" oder das Geschenk ueber einen automatischen Rabatt mit
 * Mindestbestellwert statt ueber ein 0-EUR-Produkt.
 */
(function () {
  'use strict';

  var BLOCK_MESSAGE =
    'Das Gratisgeschenk ist an den Mindestbestellwert gebunden. Es wird automatisch ' +
    'in deinen Warenkorb gelegt, sobald du die Schwelle erreichst, und kann nicht ' +
    'einzeln bestellt werden.';

  var CHECKOUT_MESSAGE =
    'Dein Warenkorb wird gerade geprueft. Bitte oeffne den Warenkorb und versuche es erneut.';

  var RECONCILE_DEBOUNCE_MS = 400;
  var FALLBACK_DELAY_MS = 5000;

  var CART_MUTATION_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  var CART_ADD_RE = /\/cart\/add(\.js)?(\?|$)/;

  // ---------------------------------------------------------------------
  // Konfiguration
  // ---------------------------------------------------------------------

  var variantIndex = null;
  var productIndex = null;

  function config() {
    return window.EM_FREE_GIFT_CONFIG || null;
  }

  function giftDefs() {
    var cfg = config();
    return cfg && Array.isArray(cfg.gifts) ? cfg.gifts : [];
  }

  function isExempt() {
    var cfg = config();
    // B2B-Kunden sind von der Geschenk-Logik ohnehin ausgenommen (siehe Drawer).
    return !cfg || !!cfg.isB2BCustomer || giftDefs().length === 0;
  }

  function buildIndex() {
    variantIndex = Object.create(null);
    productIndex = Object.create(null);
    giftDefs().forEach(function (def) {
      (def.variant_ids || []).forEach(function (id) {
        variantIndex[String(id)] = def;
      });
      if (def.variant_id) variantIndex[String(def.variant_id)] = def;
      if (def.product_id) productIndex[String(def.product_id)] = def;
      // Auswahl-Geschenke ("Kunde waehlt"): alle Produkte der Kollektion.
      (def.product_ids || []).forEach(function (id) {
        if (!productIndex[String(id)]) productIndex[String(id)] = def;
      });
    });
  }

  function giftForVariant(id) {
    if (id === null || id === undefined || id === '') return null;
    if (!variantIndex) buildIndex();
    return variantIndex[String(id)] || null;
  }

  function giftForProduct(id) {
    if (id === null || id === undefined || id === '') return null;
    if (!productIndex) buildIndex();
    return productIndex[String(id)] || null;
  }

  // Erlaubt der Drawer-Logik, das Geschenk selbst zu setzen: die Vue-Komponente
  // schreibt die Varianten-ID kurz vor dem eigenen /cart/add.js in dieses Feld.
  function isAllowedAdd(variantId) {
    var allowed = window.EM_FREE_GIFT_ALLOW_ADD;
    return allowed !== undefined && allowed !== null && String(allowed) === String(variantId);
  }

  // Warenkorb-Aenderungen, die der Drawer selbst ausloest, brauchen keinen
  // zusaetzlichen Abgleich - er aktualisiert sich ohnehin. Ohne diese Pruefung
  // laeuft parallel zum laufenden Abgleich ein zweiter an, und das Geschenk
  // wird doppelt angelegt.
  function isInternalMutation() {
    return (window.EM_FREE_GIFT_INTERNAL || 0) > 0;
  }

  // ---------------------------------------------------------------------
  // Hinweis-Ausgabe
  // ---------------------------------------------------------------------

  var noticeTimer = null;

  function notify(message) {
    try {
      var el = document.getElementById('em-gift-guard-notice');
      if (!el) {
        el = document.createElement('div');
        el.id = 'em-gift-guard-notice';
        el.setAttribute('role', 'status');
        el.setAttribute('aria-live', 'polite');
        document.body.appendChild(el);
      }
      el.textContent = message;
      el.classList.add('is-visible');
      if (noticeTimer) window.clearTimeout(noticeTimer);
      noticeTimer = window.setTimeout(function () {
        el.classList.remove('is-visible');
      }, 6000);
    } catch (error) {
      /* Hinweis ist optional - niemals die Seite blockieren. */
    }
  }

  // ---------------------------------------------------------------------
  // 1. Add-to-Cart-Sperre auf Geschenkprodukten
  // ---------------------------------------------------------------------

  var ATC_SELECTOR = [
    'button[name="add"]',
    'input[name="add"]',
    '[data-js-product-add-to-cart]',
    '.add-to-cart',
    '.product-form__submit',
    '.shopify-payment-button__button'
  ].join(',');

  // Ermittelt die Geschenk-Definition zu einem Add-to-Cart-Element, falls das
  // Element ein Geschenkprodukt in den Warenkorb legen wuerde.
  function giftForAddButton(el) {
    if (!el) return null;

    var form = el.closest ? el.closest('form') : null;
    if (form) {
      var idInput = form.querySelector('input[name="id"], select[name="id"]');
      if (idInput && idInput.value) {
        var byVariant = giftForVariant(idInput.value);
        if (byVariant) return byVariant;
      }
    }

    var holder = el.closest
      ? el.closest('[data-product-id], [data-empi], [data-epi], [data-variant-id]')
      : null;
    while (holder) {
      var byProduct =
        giftForProduct(holder.getAttribute('data-product-id')) ||
        giftForProduct(holder.getAttribute('data-empi')) ||
        giftForVariant(holder.getAttribute('data-variant-id')) ||
        giftForVariant(holder.getAttribute('data-epi'));
      if (byProduct) return byProduct;
      holder = holder.parentElement && holder.parentElement.closest
        ? holder.parentElement.closest('[data-product-id], [data-empi], [data-epi], [data-variant-id]')
        : null;
    }

    return null;
  }

  function lockAddButton(el, def) {
    if (!el || el.getAttribute('data-em-gift-locked') === 'true') return;
    el.setAttribute('data-em-gift-locked', 'true');
    el.setAttribute('aria-disabled', 'true');
    el.classList.add('em-gift-locked');
    if ('disabled' in el) {
      try {
        el.disabled = true;
      } catch (error) {
        /* z. B. bei Anchor-Elementen */
      }
    }
    var container = el.parentElement;
    if (container && !container.querySelector('.em-gift-locked-note')) {
      var note = document.createElement('p');
      note.className = 'em-gift-locked-note';
      note.textContent = BLOCK_MESSAGE;
      container.appendChild(note);
    }
    void def;
  }

  // Warenkorb-Seite (snippets/cart-form-page.liquid): Mengen-Auswahl auf
  // Geschenkzeilen sperren. Der Drawer blendet sie ueber `isGiftProduct`
  // bereits aus, die Warenkorb-Seite kennt die Geschenk-Logik nicht.
  function lockCartPageGiftLines(root) {
    if (isExempt()) return;
    var scope = root && root.querySelectorAll ? root : document;
    var lines;
    try {
      lines = scope.querySelectorAll('[data-js-cart-item][data-product-id]');
    } catch (error) {
      return;
    }
    Array.prototype.forEach.call(lines, function (line) {
      if (line.getAttribute('data-em-gift-line') === 'true') return;
      if (!giftForProduct(line.getAttribute('data-product-id'))) return;
      line.setAttribute('data-em-gift-line', 'true');
      var actions = line.querySelector('.cart-item__actions');
      var quantity = line.querySelector('product-quantity, cart-product-quantity, .quantity-selector-holder');
      if (quantity) quantity.hidden = true;
      if (actions && !actions.querySelector('.em-gift-locked-note')) {
        var note = document.createElement('span');
        note.className = 'em-gift-locked-note';
        note.textContent = 'Gratisgeschenk - Menge nicht aenderbar';
        actions.insertBefore(note, actions.firstChild);
      }
    });
  }

  function scanForGiftAddButtons(root) {
    if (isExempt()) return;
    var scope = root && root.querySelectorAll ? root : document;
    var buttons;
    try {
      buttons = scope.querySelectorAll(ATC_SELECTOR);
    } catch (error) {
      return;
    }
    Array.prototype.forEach.call(buttons, function (button) {
      var def = giftForAddButton(button);
      if (def) lockAddButton(button, def);
    });
  }

  // Klicks auf Geschenk-Add-to-Cart abfangen, bevor Theme- oder App-Handler
  // greifen (Capture-Phase). Faengt auch dynamisch nachgeladene Buttons ab,
  // die der Scan noch nicht gesehen hat.
  function onCaptureClick(event) {
    if (isExempt()) return;
    var target = event.target && event.target.closest ? event.target.closest(ATC_SELECTOR) : null;
    if (!target) return;
    if (!giftForAddButton(target)) return;
    event.preventDefault();
    event.stopPropagation();
    if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
    notify(BLOCK_MESSAGE);
  }

  function onCaptureSubmit(event) {
    if (isExempt()) return;
    var form = event.target;
    if (!form || !form.querySelector) return;
    var action = form.getAttribute('action') || '';
    if (action.indexOf('/cart/add') === -1) return;
    var idInput = form.querySelector('input[name="id"], select[name="id"]');
    if (!idInput || !giftForVariant(idInput.value)) return;
    event.preventDefault();
    event.stopPropagation();
    if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
    notify(BLOCK_MESSAGE);
  }

  // ---------------------------------------------------------------------
  // 2. Checkout-Sperre
  // ---------------------------------------------------------------------

  var CHECKOUT_SELECTOR = [
    '#CheckOut',
    'button[name="checkout"]',
    'input[name="checkout"]',
    'a[href$="/checkout"]',
    'a[href*="/checkout?"]'
  ].join(',');

  function cartStateIsInvalid() {
    var state = window.EM_FREE_GIFT_STATE;
    return !!(state && state.valid === false);
  }

  function onCaptureCheckout(event) {
    if (isExempt()) return;
    if (!cartStateIsInvalid()) return;
    var target = event.target && event.target.closest ? event.target.closest(CHECKOUT_SELECTOR) : null;
    if (!target) return;
    event.preventDefault();
    event.stopPropagation();
    if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
    notify(CHECKOUT_MESSAGE);
    scheduleReconcile();
  }

  // ---------------------------------------------------------------------
  // 3. Warenkorb-Mutationen fremder Apps erkennen
  // ---------------------------------------------------------------------

  var reconcileTimer = null;

  function scheduleReconcile() {
    if (reconcileTimer) window.clearTimeout(reconcileTimer);
    reconcileTimer = window.setTimeout(function () {
      reconcileTimer = null;
      try {
        window.dispatchEvent(new CustomEvent('em:cart-mutated'));
      } catch (error) {
        /* CustomEvent nicht verfuegbar - dann greift nur der Fallback. */
      }
      if (!window.EM_FREE_GIFT_STATE) fallbackReconcile();
    }, RECONCILE_DEBOUNCE_MS);
  }

  // Liest die Varianten-ID aus einem /cart/add.js-Request-Body (JSON, FormData
  // oder URL-encoded) und liefert die betroffene Geschenk-Definition zurueck.
  function giftFromAddBody(body) {
    if (!body) return null;
    try {
      if (typeof FormData !== 'undefined' && body instanceof FormData) {
        return giftForVariant(body.get('id'));
      }
      if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
        return giftForVariant(body.get('id'));
      }
      if (typeof body === 'string') {
        var trimmed = body.trim();
        if (trimmed.charAt(0) === '{' || trimmed.charAt(0) === '[') {
          return giftFromAddPayload(JSON.parse(trimmed));
        }
        return giftForVariant(new URLSearchParams(trimmed).get('id'));
      }
      if (typeof body === 'object') {
        return giftFromAddPayload(body);
      }
    } catch (error) {
      /* Unlesbarer Body wird nicht blockiert. */
    }
    return null;
  }

  function giftFromAddPayload(payload) {
    if (!payload) return null;
    if (Array.isArray(payload.items)) {
      for (var i = 0; i < payload.items.length; i += 1) {
        var item = payload.items[i];
        var def = giftForVariant(item && item.id);
        if (def && !isAllowedAdd(item.id)) return def;
      }
      return null;
    }
    var single = giftForVariant(payload.id);
    return single && !isAllowedAdd(payload.id) ? single : null;
  }

  function blockedAddResponse() {
    var payload = JSON.stringify({
      status: 422,
      message: BLOCK_MESSAGE,
      description: BLOCK_MESSAGE
    });
    if (typeof Response === 'function') {
      return Promise.resolve(
        new Response(payload, {
          status: 422,
          headers: { 'Content-Type': 'application/json' }
        })
      );
    }
    // Fallback fuer Umgebungen ohne Response-Konstruktor: ein abgelehnter
    // Promise wuerde bei aufrufenden Apps als Netzwerkfehler durchschlagen.
    return Promise.resolve({
      ok: false,
      status: 422,
      statusText: 'Unprocessable Entity',
      headers: { get: function () { return 'application/json'; } },
      json: function () { return Promise.resolve(JSON.parse(payload)); },
      text: function () { return Promise.resolve(payload); },
      clone: function () { return this; }
    });
  }

  function patchFetch() {
    var nativeFetch = window.fetch;
    if (typeof nativeFetch !== 'function') return;
    window.fetch = function (input, init) {
      var url = '';
      var method = 'GET';
      try {
        url = typeof input === 'string' ? input : (input && input.url) || '';
        method = String((init && init.method) || (input && input.method) || 'GET').toUpperCase();
        if (!isExempt() && method === 'POST' && CART_ADD_RE.test(url)) {
          var body = (init && init.body) || (input && input.body) || null;
          if (giftFromAddBody(body)) {
            notify(BLOCK_MESSAGE);
            return blockedAddResponse();
          }
        }
      } catch (error) {
        /* Interception darf niemals den Request kaputt machen. */
      }
      var result = nativeFetch.apply(this, arguments);
      try {
        if (!isExempt() && !isInternalMutation() && method === 'POST' && CART_MUTATION_RE.test(url)) {
          result.then(scheduleReconcile, function () {});
        }
      } catch (error) {
        /* ignorieren */
      }
      return result;
    };
  }

  function patchXhr() {
    if (typeof XMLHttpRequest !== 'function') return;
    var open = XMLHttpRequest.prototype.open;
    var send = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url) {
      try {
        this.__emGiftUrl = url;
        this.__emGiftMethod = String(method || 'GET').toUpperCase();
      } catch (error) {
        /* ignorieren */
      }
      return open.apply(this, arguments);
    };

    XMLHttpRequest.prototype.send = function (body) {
      try {
        var url = this.__emGiftUrl || '';
        var method = this.__emGiftMethod || 'GET';
        if (!isExempt() && method === 'POST' && CART_ADD_RE.test(url) && giftFromAddBody(body)) {
          notify(BLOCK_MESSAGE);
          this.abort();
          return undefined;
        }
        if (!isExempt() && !isInternalMutation() && method === 'POST' && CART_MUTATION_RE.test(url)) {
          this.addEventListener('load', scheduleReconcile);
        }
      } catch (error) {
        /* ignorieren */
      }
      return send.apply(this, arguments);
    };
  }

  // ---------------------------------------------------------------------
  // 4. Fallback: Bereinigung ohne Vue-Drawer
  // ---------------------------------------------------------------------

  var fallbackRunning = false;

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  // Zuordnung wie im Drawer (giftLineOwner): Marker-Property zuerst, weil ein
  // Auswahl-Geschenk in mehreren Kollektionen liegen kann.
  function lineOwner(item) {
    if (!item) return null;
    var defs = giftDefs();
    for (var i = 0; i < defs.length; i += 1) {
      if (item.properties && item.properties[defs[i].property]) return defs[i];
    }
    return giftForVariant(item.variant_id) || giftForProduct(item.product_id) || null;
  }

  function lineIsGift(item) {
    if (!item) return false;
    if (giftForVariant(item.variant_id)) return true;
    if (giftForProduct(item.product_id)) return true;
    return giftDefs().some(function (def) {
      return !!(item.properties && item.properties[def.property]);
    });
  }

  function fallbackReconcile() {
    if (isExempt() || fallbackRunning) return;
    fallbackRunning = true;
    fetch(root() + 'cart.js', { credentials: 'same-origin' })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        var items = cart.items || [];
        // Gleiche Formel wie qualifyingTotal() im Drawer: Warenkorbwert nach
        // allen Rabatten, ohne Geschenkzeilen.
        var giftValue = items.reduce(function (sum, item) {
          if (!lineIsGift(item)) return sum;
          var linePrice = item.final_line_price != null ? item.final_line_price : item.line_price;
          return sum + (linePrice || 0);
        }, 0);
        var qualifying = typeof cart.total_price === 'number'
          ? Math.max(0, cart.total_price - giftValue)
          : items.reduce(function (sum, item) {
              if (lineIsGift(item)) return sum;
              var linePrice = item.final_line_price != null ? item.final_line_price : item.line_price;
              return sum + (linePrice || 0);
            }, 0);

        var updates = {};
        var hasUpdates = false;

        giftDefs().forEach(function (def) {
          var lines = items.filter(function (item) {
            return lineOwner(item) === def;
          });
          if (!lines.length) return;
          if (qualifying < def.threshold) {
            // Schwelle nicht erreicht -> Geschenk darf nicht im Warenkorb liegen.
            lines.forEach(function (line) {
              updates[line.key] = 0;
              hasUpdates = true;
            });
            return;
          }
          // Schwelle erreicht: hoechstens 1x, alles Weitere entfernen.
          lines.forEach(function (line, index) {
            if (index === 0 && line.quantity === 1) return;
            updates[line.key] = index === 0 ? 1 : 0;
            hasUpdates = true;
          });
        });

        if (!hasUpdates) return null;
        return fetch(root() + 'cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates: updates })
        });
      })
      .catch(function () {
        /* Fallback ist best effort. */
      })
      .then(function () {
        fallbackRunning = false;
      });
  }

  // ---------------------------------------------------------------------
  // Initialisierung
  // ---------------------------------------------------------------------

  function observeDom() {
    if (typeof MutationObserver !== 'function' || !document.body) return;
    var pending = null;
    var observer = new MutationObserver(function () {
      if (pending) return;
      pending = window.setTimeout(function () {
        pending = null;
        scanForGiftAddButtons(document);
        lockCartPageGiftLines(document);
      }, 250);
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  function init() {
    // Interception immer installieren: die Konfiguration wird von der
    // Drawer-Section geschrieben und kann spaeter im Dokument stehen als
    // dieses Skript. isExempt() wird deshalb bei jedem Aufruf neu geprueft.
    patchFetch();
    patchXhr();

    document.addEventListener('click', onCaptureClick, true);
    document.addEventListener('click', onCaptureCheckout, true);
    document.addEventListener('submit', onCaptureSubmit, true);

    scanForGiftAddButtons(document);
    lockCartPageGiftLines(document);
    observeDom();

    window.addEventListener('em:free-gift-config', function () {
      variantIndex = null;
      productIndex = null;
      scanForGiftAddButtons(document);
      lockCartPageGiftLines(document);
    });

    // Falls der Vue-Drawer nicht laeuft (Section deaktiviert, JS-Fehler),
    // uebernimmt der Fallback die Bereinigung.
    window.setTimeout(function () {
      if (!window.EM_FREE_GIFT_STATE) fallbackReconcile();
    }, FALLBACK_DELAY_MS);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
